/**
 * Global background-tasks tray — the persistent, non-blocking progress widget
 * (the Notion pattern). Mounted once at the app root; renders the
 * upload-manager store's in-flight items so the user can navigate freely while
 * work runs in the background.
 *
 * Two item types share the tray:
 *  - UploadItem              — resumable file byte-transfer (progress 0–100)
 *  - ConnectorImportPageItem — per-page connector import row (status via socket)
 *
 * Shows an explicit "keep this tab open" line while file imports are
 * still transferring bytes (byte-transfer is the only window where a tab-close
 * would lose work; connector-import pages are server-side so they survive).
 */

import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Link,
  Progress,
  Text,
  VisuallyHidden,
  VStack,
  usePrefersReducedMotion,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { MaterialIcon } from '../ui/MaterialIcon';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { safeAnalytics } from '../../utils/analytics';
import { getArtifactRoute } from '../../utils/artifactCapabilities';
import { useScreenWakeLock } from '../../hooks/ui/useScreenWakeLock';
import {
  selectHasActiveItems,
  selectHasActiveUploads,
  useUploadManagerStore,
  type ConnectorImportPageItem,
  type TrayItem,
  type UploadItem,
} from '../../stores/uploadManagerStore';

// ---------------------------------------------------------------------------
// Upload row (byte-transfer)
// ---------------------------------------------------------------------------

function UploadStatusIcon({ status }: { status: UploadItem['status'] }) {
  if (status === 'done')
    return <MaterialIcon name="check_circle" color="text.success" boxSize={5} />;
  if (status === 'error') return <MaterialIcon name="error" color="text.error" boxSize={5} />;
  if (status === 'processing')
    return <MaterialIcon name="hourglass_empty" color="text.muted" boxSize={5} />;
  return <MaterialIcon name="upload" color="text.muted" boxSize={5} />;
}

function UploadRow({ item }: { item: UploadItem }) {
  const cancel = useUploadManagerStore((s) => s.cancel);
  const remove = useUploadManagerStore((s) => s.remove);
  // Only the byte transfer is cancellable — once the bytes have landed the work
  // is server-side, so the button dismisses the row instead.
  const currentWorkspaceId = useWorkspaceId();
  const finished = item.status !== 'uploading';
  // Link only once the artifact is usable: `processing` (a video still
  // transcoding) has no playable page yet. Finished rows stay until cleared,
  // and switching workspace keeps the tray, so a row from another workspace
  // stays plain text: its page would 404 under the current workspace.
  const route =
    item.status === 'done' &&
    item.artifactId &&
    item.artifactType &&
    item.workspaceId === currentWorkspaceId
      ? getArtifactRoute(item.artifactType, item.artifactId)
      : null;

  return (
    <HStack spacing={3} w="full" py={2} align="center">
      <UploadStatusIcon status={item.status} />
      <Box flex={1} minW={0}>
        {route ? (
          <Link
            as={RouterLink}
            to={route}
            fontSize="sm"
            noOfLines={1}
            title={item.fileName}
            onClick={() =>
              safeAnalytics.track(ANALYTICS_EVENTS.UPLOAD_TRAY_ITEM_OPENED, {
                artifact_type: item.artifactType,
                intent: item.intent,
              })
            }
          >
            {item.fileName}
          </Link>
        ) : (
          <Text fontSize="sm" color="text.primary" noOfLines={1} title={item.fileName}>
            {item.fileName}
          </Text>
        )}
        {item.status === 'uploading' && (
          <Progress
            value={item.progress}
            size="xs"
            mt={1}
            borderRadius="full"
            aria-label={`Uploading ${item.fileName}, ${item.progress}%`}
          />
        )}
        {/* The upload succeeded; the file just isn't usable yet. Say which, and
            say it is safe to leave — the single most load-bearing line in this
            component, because the alternative reading is "it failed".
            Media-neutral wording on purpose: `transcoding` is not video-only,
            an ambiguous .webm/.ogg container passes through it too. */}
        {item.status === 'processing' && (
          <Text fontSize="xs" color="text.muted" noOfLines={2}>
            {item.stoppedWatching
              ? 'Still processing. It will appear in your workspace when it is ready.'
              : 'Uploaded. Processing: this can take a couple of minutes. You can close this.'}
          </Text>
        )}
        {/* Static on purpose: the row goes green when the bytes land, long
            before a spreadsheet import finishes, so the import's outcome is
            reported by the entity icon and the completion toast. A
            kept-as-file reason also re-expands the tray and is announced in
            its live region. */}
        {item.status === 'done' && item.note && (
          <Text fontSize="xs" color="text.muted" noOfLines={2}>
            {item.note}
          </Text>
        )}
        {item.status === 'error' && (
          <Text fontSize="xs" color="text.error" noOfLines={2} role="alert">
            {item.error ?? 'Upload failed'}
          </Text>
        )}
      </Box>
      <IconButton
        aria-label={finished ? `Dismiss ${item.fileName}` : `Cancel upload of ${item.fileName}`}
        icon={<MaterialIcon name="close" boxSize={4} />}
        size="xs"
        variant="ghost"
        onClick={() => (finished ? remove(item.id) : cancel(item.id))}
      />
    </HStack>
  );
}

// ---------------------------------------------------------------------------
// Connector-import page row
// ---------------------------------------------------------------------------

function ImportPageStatusIcon({ status }: { status: ConnectorImportPageItem['status'] }) {
  if (status === 'done')
    return <MaterialIcon name="check_circle" color="text.success" boxSize={5} />;
  if (status === 'error') return <MaterialIcon name="error" color="text.error" boxSize={5} />;
  if (status === 'skipped') return <MaterialIcon name="skip_next" color="text.muted" boxSize={5} />;
  // pending
  return <MaterialIcon name="hourglass_empty" color="text.muted" boxSize={5} />;
}

function ImportPageRow({ item }: { item: ConnectorImportPageItem }) {
  const remove = useUploadManagerStore((s) => s.remove);
  const finished = item.status !== 'pending';

  return (
    <HStack spacing={3} w="full" py={2} align="center">
      <ImportPageStatusIcon status={item.status} />
      <Box flex={1} minW={0}>
        <Text fontSize="sm" color="text.primary" noOfLines={1} title={item.pageName}>
          {item.pageName}
        </Text>
        {item.status === 'pending' && (
          <Text fontSize="xs" color="text.muted">
            Importing…
          </Text>
        )}
        {item.status === 'skipped' && (
          <Text fontSize="xs" color="text.muted">
            Already exists
          </Text>
        )}
        {item.status === 'error' && (
          <Text fontSize="xs" color="text.error" noOfLines={1} role="alert">
            {item.error ?? 'Import failed'}
          </Text>
        )}
      </Box>
      {finished && (
        <IconButton
          aria-label={`Dismiss ${item.pageName}`}
          icon={<MaterialIcon name="close" boxSize={4} />}
          size="xs"
          variant="ghost"
          onClick={() => remove(item.id)}
        />
      )}
    </HStack>
  );
}

// ---------------------------------------------------------------------------
// Dispatch row
// ---------------------------------------------------------------------------

function TrayRow({ item }: { item: TrayItem }) {
  if (item.kind === 'upload') return <UploadRow item={item} />;
  return <ImportPageRow item={item} />;
}

// ---------------------------------------------------------------------------
// Tray
// ---------------------------------------------------------------------------

// Gap (px) left between the top of the tray and the bottom of the toast stack
// so they never touch. Toasts are lifted by (tray height + this) via the CSS
// variable below.
const TRAY_TOAST_GAP = 12;

// Never lift the toast stack so far it leaves the toasts less than this much
// headroom from the top of the viewport. On a short (landscape-mobile) viewport
// an EXPANDED tray can be nearly as tall as the screen; without this clamp the
// offset would push a firing toast off the top edge. When the clamp bites, the
// toast overlaps the tray's upper portion instead — a visible degraded state
// beats an invisible one (rule 12). No effect on desktop/portrait, where the
// tray is a fraction of the viewport height.
const MIN_TOAST_HEADROOM = 96;

export function UploadTray() {
  const items = useUploadManagerStore((s) => s.items);
  const hasActive = useUploadManagerStore(selectHasActiveItems);
  const clearFinished = useUploadManagerStore((s) => s.clearFinished);

  // Hold the screen awake while any managed upload is transferring bytes — the
  // same "keep this tab open" window the tray warns about below. Drag-drop
  // video/attachment uploads and connector file-imports run through the shared
  // resumable transport (uploadManagerStore); without this, a large upload
  // started on a phone lets the screen sleep mid-transfer — the bug #715 fixed
  // for voice capture, one surface over. This tray is mounted once at the app
  // root, so a single instance here covers every upload surface.
  //
  // Byte-transfer only (selectHasActiveUploads, not selectHasActiveItems):
  // connector-import *pages* are server-side and survive a sleeping tab, so
  // they don't need the lock. Deriving the boolean from the store means one
  // lock is held while any upload runs and released the moment the last one
  // finishes — concurrent uploads share it rather than double-holding.
  const hasActiveUploads = useUploadManagerStore(selectHasActiveUploads);
  // `source: 'upload_manager'` keeps these events separable from the other
  // 'upload'-phase surfaces (voice transcription, mobile capture) on the
  // wake-lock dashboards.
  useScreenWakeLock(hasActiveUploads, 'upload', 'upload_manager');
  const [collapsed, setCollapsed] = useState(false);
  const reduceMotion = usePrefersReducedMotion();
  const trayRef = useRef<HTMLDivElement>(null);

  // Auto-expand on a NEW error so a collapsed tray can't silently hide a failure
  // (the per-row error UI lives inside the Collapse). Matches the Notion pattern.
  const errorCount = items.filter(
    (it) =>
      (it.kind === 'upload' && it.status === 'error') ||
      (it.kind === 'connector-import-page' && it.status === 'error'),
  ).length;
  // A "kept as a file" reason pins the tray the same way an error does: it is
  // the only place the user learns why the file did not become a spreadsheet.
  const keptAsFileRows = items.filter(
    (it): it is UploadItem => it.kind === 'upload' && it.status === 'done' && !!it.keptAsFile,
  );
  // Both need the user's eyes, so a new one of either re-expands the tray.
  const attentionCount = errorCount + keptAsFileRows.length;
  const prevAttentionCount = useRef(0);
  useEffect(() => {
    if (attentionCount > prevAttentionCount.current) setCollapsed(false);
    prevAttentionCount.current = attentionCount;
  }, [attentionCount]);

  // Derived counts — must be computed before any early return to avoid
  // "rendered fewer hooks" violations. All are pure derivations of `items`.
  const uploadingCount = items.filter(
    (it) => it.kind === 'upload' && it.status === 'uploading',
  ).length;
  // `stoppedWatching` rows are excluded: the heading must not assert live work
  // we explicitly stopped following.
  const processingCount = items.filter(
    (it) => it.kind === 'upload' && it.status === 'processing' && !it.stoppedWatching,
  ).length;
  const importingPageCount = items.filter(
    (it) => it.kind === 'connector-import-page' && it.status === 'pending',
  ).length;
  // Show the "keep tab open" warning only while a file-import byte-transfer is in progress.
  // Server-side connector imports are safe to leave — they don't need the tab.
  const fileImportActive = items.some(
    (it) => it.kind === 'upload' && it.status === 'uploading' && it.intent === 'import',
  );

  // Publish the tray's live footprint (distance from the viewport bottom to the
  // top of the tray, plus a gap) as `--upload-tray-offset` on the document root.
  // The bottom-anchored Chakra toast containers read it (theme/styles.ts) and
  // translate up by that amount, so toasts stack ABOVE the tray instead of
  // overlapping it in the bottom-right corner. A ResizeObserver keeps it in sync
  // through expand/collapse animations and content changes; the effect resets to
  // 0px whenever the tray is absent (no items) or unmounts, so toasts drop back
  // to the corner.
  useEffect(() => {
    const el = trayRef.current;
    const root = document.documentElement;
    if (!el) {
      root.style.setProperty('--upload-tray-offset', '0px');
      return;
    }
    const update = () => {
      const rect = el.getBoundingClientRect();
      const raw = window.innerHeight - rect.top + TRAY_TOAST_GAP;
      // Clamp so a tall tray on a short viewport can't lift toasts off the top.
      const ceiling = Math.max(0, window.innerHeight - MIN_TOAST_HEADROOM);
      const offset = Math.max(0, Math.min(raw, ceiling));
      root.style.setProperty('--upload-tray-offset', `${offset}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      root.style.setProperty('--upload-tray-offset', '0px');
    };
  }, [items.length, collapsed, fileImportActive]);

  // What the persistent region says right now. Empty when there is nothing to
  // announce; the processing→done transition is the one a waiting user needs.
  const doneCount = items.filter((it) => it.kind === 'upload' && it.status === 'done').length;
  const liveAnnouncement = (() => {
    if (processingCount > 0) {
      return `Upload complete. Processing ${processingCount} file${processingCount > 1 ? 's' : ''}.`;
    }
    if (keptAsFileRows.length > 0) {
      return keptAsFileRows.map((it) => `${it.fileName}: ${it.note}`).join(' ');
    }
    if (doneCount > 0) return 'Processing finished. Your upload is ready.';
    return '';
  })();

  if (items.length === 0) return null;

  const headingText = (() => {
    if (uploadingCount > 0 && importingPageCount > 0) {
      return `Uploading ${uploadingCount} file${uploadingCount > 1 ? 's' : ''} · ${importingPageCount} page${importingPageCount > 1 ? 's' : ''} importing…`;
    }
    if (uploadingCount > 0) {
      return `Uploading ${uploadingCount} file${uploadingCount > 1 ? 's' : ''}…`;
    }
    if (importingPageCount > 0) {
      return `Importing ${importingPageCount} page${importingPageCount > 1 ? 's' : ''}…`;
    }
    // Bytes are in; the server is still working. A collapsed tray must still say
    // so, or the only remaining signal is a heading that reads "Background tasks".
    if (processingCount > 0) {
      return `Processing ${processingCount} file${processingCount > 1 ? 's' : ''}…`;
    }
    return 'Background tasks';
  })();

  return (
    <Box
      ref={trayRef}
      position="fixed"
      bottom={{ base: 2, md: 4 }}
      right={{ base: 2, md: 4 }}
      zIndex="toast"
      w={{ base: 'calc(100vw - 1rem)', md: '22rem' }}
      maxW="22rem"
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="lg"
      boxShadow="lg"
      overflow="hidden"
      role="region"
      aria-label="Background tasks"
    >
      {/* Persistent live region. Mounted with the tray, not with the message,
          so assistive tech is already monitoring it when the text changes — a
          region rendered together with its content announces nothing. */}
      <VisuallyHidden role="status" aria-live="polite">
        {liveAnnouncement}
      </VisuallyHidden>
      <Flex align="center" justify="space-between" pr={2}>
        {/* Collapse toggle — a real button, with the Clear button as a SIBLING
            (never nest interactive elements). */}
        <Flex
          as="button"
          flex={1}
          minW={0}
          align="center"
          justify="space-between"
          px={4}
          py={3}
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand background tasks' : 'Collapse background tasks'}
        >
          <Text fontSize="sm" fontWeight="semibold" color="text.primary" noOfLines={1}>
            {headingText}
          </Text>
          <MaterialIcon
            name={collapsed ? 'expand_less' : 'expand_more'}
            boxSize={5}
            color="text.muted"
            ml={2}
          />
        </Flex>
        {!hasActive && (
          <Button size="xs" variant="ghost" color="text.muted" onClick={clearFinished}>
            Clear
          </Button>
        )}
      </Flex>

      {fileImportActive && (
        <Box px={4} py={2} bg="bg.warning" aria-live="polite">
          <Text fontSize="xs" color="text.warning">
            Keep this tab open until your import finishes uploading.
          </Text>
        </Box>
      )}

      <Collapse in={!collapsed} animateOpacity={!reduceMotion}>
        <VStack
          spacing={0}
          align="stretch"
          px={4}
          pb={3}
          maxH="16rem"
          overflowY="auto"
          divider={<Box borderBottomWidth="1px" borderColor="border.subtle" />}
        >
          {items.map((item) => (
            <TrayRow key={item.id} item={item} />
          ))}
        </VStack>
      </Collapse>
    </Box>
  );
}
